/**
 * VelocityAlerts - Display and manage velocity alerts
 * 
 * Shows alerts for significant price movements in portfolio positions.
 */

import { useState, useEffect } from 'react';
import { AlertTriangle, TrendingUp, TrendingDown, X, CheckCircle, RefreshCw } from 'lucide-react';
import { fetchApi } from '../api/client';

export default function VelocityAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  const fetchAlerts = async () => {
    try {
      const data = await fetchApi('/velocity/alerts');
      setAlerts(data);
    } catch (err) {
      console.error('Error fetching alerts:', err);
    } finally {
      setLoading(false);
    }
  };

  const checkForNewAlerts = async () => {
    setChecking(true);
    try {
      const data = await fetchApi('/velocity/alerts/check', { method: 'POST' });
      if (data.count > 0) {
        // Refresh alerts list
        fetchAlerts();
      }
    } catch (err) {
      console.error('Error checking alerts:', err);
    } finally {
      setChecking(false);
    }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      await fetchApi(`/velocity/alerts/${alertId}/acknowledge`, { method: 'POST' });
      setAlerts(alerts.filter(a => a.id !== alertId));
    } catch (err) {
      console.error('Error acknowledging alert:', err);
    }
  };

  const acknowledgeAll = async () => {
    try {
      await fetchApi('/velocity/alerts/acknowledge-all', { method: 'POST' });
      setAlerts([]);
    } catch (err) {
      console.error('Error acknowledging all alerts:', err);
    }
  };

  useEffect(() => {
    fetchAlerts();
    // Also check for new alerts on mount
    checkForNewAlerts();
  }, []);

  // Don't render if no alerts
  if (!loading && alerts.length === 0) {
    return null;
  }

  const formatDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div className="card border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-amber-600/5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-amber-400 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          Velocity Alerts
          {alerts.length > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs bg-amber-500/20 rounded-full">
              {alerts.length}
            </span>
          )}
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={checkForNewAlerts}
            disabled={checking}
            className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-700 rounded transition-colors"
            title="Check for new alerts"
          >
            <RefreshCw className={`w-4 h-4 ${checking ? 'animate-spin' : ''}`} />
          </button>
          {alerts.length > 0 && (
            <button
              onClick={acknowledgeAll}
              className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1"
            >
              <CheckCircle className="w-3 h-3" />
              Clear All
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-4">
          <div className="spinner w-5 h-5"></div>
        </div>
      ) : (
        <div className="space-y-2">
          {alerts.map((alert) => (
            <div 
              key={alert.id}
              className={`flex items-center justify-between p-3 rounded-lg ${
                alert.alert_type === 'surge' 
                  ? 'bg-emerald-500/10 border border-emerald-500/20' 
                  : 'bg-red-500/10 border border-red-500/20'
              }`}
            >
              <div className="flex items-center gap-3">
                {alert.alert_type === 'surge' ? (
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                ) : (
                  <TrendingDown className="w-5 h-5 text-red-400" />
                )}
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-200">{alert.ticker}</span>
                    <span className={`font-mono text-sm ${
                      alert.alert_type === 'surge' ? 'text-emerald-400' : 'text-red-400'
                    }`}>
                      {alert.return_pct > 0 ? '+' : ''}{alert.return_pct.toFixed(1)}%
                    </span>
                    <span className="text-xs text-slate-500">({alert.period})</span>
                  </div>
                  <div className="text-xs text-slate-500">
                    {formatDate(alert.triggered_at)}
                    {alert.price_at_alert && (
                      <span> • ${alert.price_at_alert.toFixed(2)}</span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => acknowledgeAlert(alert.id)}
                className="p-1 text-slate-500 hover:text-slate-300 hover:bg-slate-700 rounded"
                title="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
